import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import {
  selectUpgradeState,
  setBillingCycle,
  setCoupon,
  setSeatCount,
  setSelectedPlan,
} from '@/app/redux/slices/upgradeSlice';
import { getRecommendedPlan } from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import PlanDetails from '@/dashboard/Upgrade/PlanDetails';
import PlanPricing from '@/dashboard/Upgrade/PlanPricing';
import api from '@/db/apiClient';
import { ArrowRightIcon, InfoIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Input } from '@shadcn/components/input';
import { Separator } from '@shadcn/components/separator';
import { Tabs, TabsList, TabsTrigger } from '@shadcn/components/tabs';
import { cn } from '@shadcn/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PricingTable() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const studentCount = useOrganisation().organisation?.studentCount || 1;

  const { seatCount, selectedPlan, billingCycle, currency, coupon } =
    useAppSelector(selectUpgradeState);

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(seatCount?.toString() ?? '1');
  const movedToMonthly = useRef(false);
  const [discountCodeStatus, setDiscountCodeStatus] = useState<
    'loading' | 'valid' | 'invalid' | 'error' | null
  >(coupon ? 'valid' : null);
  const [discountCode, _setDiscountCode] = useState(coupon?.code ?? '');
  const setDiscountCode = (value: string) =>
    _setDiscountCode(value.toUpperCase());

  const recommended = useMemo(() => getRecommendedPlan(seatCount), [seatCount]);

  useEffect(() => {
    if (seatCount === null) {
      dispatch(setSeatCount(studentCount));
      setInputValue(studentCount.toString());
    } else if (seatCount < 1) {
      dispatch(setSeatCount(1));
      setInputValue('1');
    } else {
      setInputValue(seatCount.toString());
    }
  }, [seatCount, dispatch, studentCount]);

  useEffect(() => {
    if (billingCycle === 'monthly') {
      dispatch(setSelectedPlan('family'));
      movedToMonthly.current = true;
    } else if (movedToMonthly.current) {
      dispatch(setSelectedPlan(recommended));
      movedToMonthly.current = false;
    }
  }, [billingCycle, recommended, dispatch]);

  useEffect(() => {
    dispatch(setSelectedPlan(recommended));
  }, [dispatch, recommended]);

  const handleSeatCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    const numericValue = Number(value);
    if (!isNaN(numericValue) && numericValue >= 1) {
      dispatch(setSeatCount(numericValue));
    }
  };

  const handleSeatCountBlur = () => {
    const numericValue = Number(inputValue);
    if (isNaN(numericValue) || numericValue < 1) {
      setInputValue('1');
      dispatch(setSeatCount(1));
    }
  };

  const handleApplyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    try {
      setDiscountCodeStatus('loading');

      // To show proper revalidation
      await new Promise((r) => setTimeout(r, 500));

      dispatch(setCoupon(null));
      const coupon = await api.asEducator.getCouponFromCode({
        code: discountCode.trim(),
      });
      if (coupon) {
        setDiscountCodeStatus('valid');
        dispatch(setCoupon(coupon));
        setDiscountCode(coupon.code);
      } else {
        setDiscountCodeStatus('invalid');
      }
    } catch (e) {
      console.error('Error applying discount code:', e);
      setDiscountCodeStatus('error');
    }
  };

  const handleRemoveDiscountCode = () => {
    setDiscountCode('');
    setDiscountCodeStatus(null);
    dispatch(setCoupon(null));
  };

  const handleNext = () => {
    trackMixpanelEvent('Proceeded from Pricing to Payment Info', {
      selectedPlan,
      seatCount,
      billingCycle,
      currency,
      studentCount,
    });
    navigate('/checkout/payment-details');
  };

  return (
    <Tabs defaultValue="1year" className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-1">
          <span className="font-medium flex items-center gap-2">
            <span className="text-2xl">1</span>
            How many learner accounts do you need?
          </span>
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center md:ml-4 mt-2">
            <Input
              ref={inputRef}
              className="w-24 text-md m-1"
              type="number"
              min="1"
              step="1"
              value={inputValue}
              onChange={handleSeatCountChange}
              onBlur={handleSeatCountBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  inputRef.current?.blur();
                }
              }}
            />
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <InfoIcon size={16} />
              <div className="flex flex-col">
                <span>
                  Not sure? Start small — only pay for what you need now
                </span>
                <span>We'll recommend the best plan for your needs</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-medium flex items-center gap-2">
            <span className="text-2xl">2</span>
            Choose your billing period
          </span>
          <TabsList className="justify-start w-fit h-14 gap-2 bg-muted/50 md:ml-4">
            <TabsTrigger
              value="monthly"
              onClick={() => dispatch(setBillingCycle('monthly'))}
              className="data-[state=active]:bg-primary data-[state=active]:text-white h-full"
            >
              Monthly
            </TabsTrigger>
            <TabsTrigger
              value="1year"
              onClick={() => dispatch(setBillingCycle('1year'))}
              className="data-[state=active]:bg-primary data-[state=active]:text-white h-full"
            >
              1 Year
            </TabsTrigger>
            <TabsTrigger
              value="2year"
              onClick={() => dispatch(setBillingCycle('2year'))}
              className="data-[state=active]:bg-primary data-[state=active]:text-white flex flex-col gap-0 h-full"
            >
              2 Years <span className="text-xs">(10% off)</span>
            </TabsTrigger>
            <TabsTrigger
              value="3year"
              onClick={() => dispatch(setBillingCycle('3year'))}
              className="data-[state=active]:bg-primary data-[state=active]:text-white flex flex-col gap-0 h-full"
            >
              3 Years <span className="text-xs">(20% off)</span>
            </TabsTrigger>
          </TabsList>
          <div className="text-xs text-muted-foreground flex items-center gap-1 md:ml-4">
            <InfoIcon size={16} />
            You can cancel or modify your subscription at any time
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
        <div className="flex flex-col gap-2 w-full">
          <span className="font-medium flex items-center gap-2">
            <span className="text-2xl">3</span>
            Choose your plan
          </span>
          {billingCycle === 'monthly' ? (
            <div className="h-full mx-2 flex items-center gap-4">
              <PlanPricing
                plan="family"
                billingCycle={billingCycle}
                currency={currency}
                recommended={recommended === 'family'}
                selected={selectedPlan === 'family'}
                onSelect={() => dispatch(setSelectedPlan('family'))}
              />
            </div>
          ) : (
            <div className="h-full mx-2 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              <PlanPricing
                plan="family"
                billingCycle={billingCycle}
                currency={currency}
                recommended={recommended === 'family'}
                selected={selectedPlan === 'family'}
                onSelect={() => dispatch(setSelectedPlan('family'))}
              />
              <PlanPricing
                plan="small"
                billingCycle={billingCycle}
                currency={currency}
                recommended={recommended === 'small'}
                selected={selectedPlan === 'small'}
                onSelect={() => dispatch(setSelectedPlan('small'))}
              />
              <PlanPricing
                plan="medium"
                billingCycle={billingCycle}
                currency={currency}
                recommended={recommended === 'medium'}
                selected={selectedPlan === 'medium'}
                onSelect={() => dispatch(setSelectedPlan('medium'))}
              />
              <PlanPricing
                plan="large"
                billingCycle={billingCycle}
                currency={currency}
                recommended={recommended === 'large'}
                selected={selectedPlan === 'large'}
                onSelect={() => dispatch(setSelectedPlan('large'))}
              />
            </div>
          )}
        </div>
        <Separator className="lg:hidden mt-4 w-[95%] mx-auto" />
        <div className="flex flex-col justify-end gap-4 w-full lg:w-auto lg:min-w-[280px] px-2 lg:px-0 mt-4 lg:mt-8">
          <div className="flex flex-col gap-1 mt-2">
            <div className="flex items-center gap-2 justify-between">
              <span className="font-medium">Discount Code</span>
              {discountCodeStatus === 'valid' && (
                <span className="ml-2 text-sm text-rag-green">
                  Discount applied! <span className="text-base">🥳</span>
                </span>
              )}
              {discountCodeStatus === 'invalid' && (
                <span className="ml-2 text-sm text-rag-red">
                  Code not found
                </span>
              )}
              {discountCodeStatus === 'error' && (
                <span className="ml-2 text-sm text-rag-red">
                  Something went wrong. Please refresh the page and try again.
                </span>
              )}
            </div>
            <div className="relative flex items-center gap-2">
              <Input
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                className={cn(
                  discountCodeStatus === 'valid' &&
                    'disabled:opacity-100 disabled:cursor-default',
                )}
                placeholder="Enter code"
                disabled={
                  discountCodeStatus === 'loading' ||
                  discountCodeStatus === 'valid'
                }
              />
              {discountCodeStatus === 'valid' && (
                <XIcon
                  className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer"
                  onClick={handleRemoveDiscountCode}
                />
              )}
              {discountCodeStatus !== 'valid' && (
                <Button
                  onClick={handleApplyDiscountCode}
                  loading={discountCodeStatus === 'loading'}
                >
                  Apply
                </Button>
              )}
            </div>
          </div>
          <PlanDetails
            plan={selectedPlan}
            seatCount={seatCount ?? 1}
            billingCycle={billingCycle}
            currency={currency}
            coupon={coupon}
          />
          <Button className="w-full lg:w-auto gap-2" onClick={handleNext}>
            Continue setting up your plan
            <ArrowRightIcon size={16} />
          </Button>
        </div>
      </div>
    </Tabs>
  );
}
